import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LandingBottomNav, { TABS } from "@/components/LandingBottomNav";

/**
 * AppShell — mobile-first 6-tab layout for the CartIQ 360 dashboard.
 *
 * - Mobile (<lg): the shared `LandingBottomNav` is mounted inside this
 *   shell so users get the same 6-tab bar everywhere.
 * - Desktop (>=lg): a left sidebar (w-64) with the same 6 sections. In-app
 *   tabs (Home/Spending/Budget) call `onChange`; external tabs
 *   (Upload/Intelligence/Pricing) navigate via react-router.
 *
 * The shell does NOT manage data — it's a presentational wrapper. The
 * parent (Dashboard.jsx) controls `active` for the in-app tabs and slots
 * tab content into `children`.
 */

// Tab IDs that are handled in-app (via the parent's onChange hash router).
// Upload was previously in-app but its dashboard view was a stub — it now
// always navigates to /upload so users land on the real drop-zone page.
const INAPP_TAB_IDS = new Set(["home", "spending", "budget"]);

export default function AppShell({ active, onChange, header, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSidebarTab = (tab) => {
    // In-app tab → switch dashboard tab in place.
    if (INAPP_TAB_IDS.has(tab.id)) {
      const target = `${location.pathname}#${tab.dashHash || tab.id}`;
      navigate(target);
      onChange?.(tab.id);
      return;
    }
    if (tab.route) navigate(tab.route);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-[#F0EDE8] flex">
      {/* Desktop sidebar — visible only on lg+ */}
      <aside
        className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 border-r border-[#2A2A35] bg-[#0A0A0F] z-30"
        data-testid="sidebar-nav"
      >
        <div className="px-5 py-6 border-b border-[#2A2A35]">
          <div className="flex items-center gap-3">
            <img
              src="/cartiq360-logo-256.png"
              alt="CartIQ 360"
              className="w-16 h-16 object-contain shrink-0"
              data-testid="sidebar-brand-logo"
            />
            <div>
              <div
                className="font-display uppercase text-2xl tracking-wide leading-none"
                style={{ fontFamily: "'Pirata One', serif" }}
              >
                CartIQ <span style={{ color: "#FF6B00" }}>360</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] mt-1.5">
                By Solutionist
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = INAPP_TAB_IDS.has(t.id) && active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSidebarTab(t)}
                data-testid={`sidebar-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors text-sm uppercase tracking-[0.18em] font-bold ${
                  isActive
                    ? "bg-[#FF6B00]/10 text-[#FF6B00] border-l-2 border-[#FF6B00]"
                    : "text-[#888] border-l-2 border-transparent hover:text-[#F0EDE8] hover:bg-[#14141A]"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="px-6 py-4 text-[10px] uppercase tracking-[0.18em] text-[#555] border-t border-[#2A2A35]">
          v1.0 · CartIQ 360
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 lg:ml-64 min-h-screen flex flex-col min-w-0 overflow-x-hidden">
        {header}
        <main
          className="flex-1 pb-24 lg:pb-8 min-w-0 overflow-x-hidden"
          data-testid={`tab-content-${active}`}
        >
          {children}
        </main>

        {/* Shared bottom nav for mobile — same as public pages */}
        <LandingBottomNav />
      </div>
    </div>
  );
}
